// The workspace's places beside the conversation: Library (everything the EmCog made), Schedule (one timeline of what's
// coming), Activity (his own time, as it happened) and Town. Each reads /api/workspace/* with the PIN token.
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { BenchView, WorkbenchContext } from '../../contexts/WorkbenchContext';
import ActivityPanel from '../ActivityPanel';
import { useFeatures } from '../../hooks/useFeatures';
import { openLinkFromDesktop } from '../../utils/desktop';
import AddToSchedule, { ScheduleDetail } from './AddToSchedule';

/** Reads a workspace route (null = this EmCog doesn't have it: nothing is fetched). */
export function useWorkspaceData<T>(path: string | null, refreshMs = 60_000): { data: T | null; error: string | null; reload: () => void } {
  const { token } = useAuth();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    if (!path) return;
    try {
      const res = await fetch(path, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(`Couldn't load this (${res.status}).`);
      setData(await res.json()); setError(null);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }, [path, token]);
  useEffect(() => { reload(); const t = setInterval(reload, refreshMs); return () => clearInterval(t); }, [reload, refreshMs]);
  // Something was just added from the workspace: show it now, not at the next refresh.
  useEffect(() => {
    const evs = ['emcog:schedule-changed', 'emcog:workspace-changed'];
    evs.forEach(e => window.addEventListener(e, reload));
    return () => evs.forEach(e => window.removeEventListener(e, reload));
  }, [reload]);
  return { data, error, reload };
}

/** A change through /api/workspace with the token; throws the server's words, and tells the rest of the workspace. */
export function useWorkspaceCall(): (path: string, method?: string, body?: any) => Promise<any> {
  const { token } = useAuth();
  return useCallback(async (path: string, method = 'POST', body?: any) => {
    const res = await fetch(path, { method, headers: { ...(body ? { 'Content-Type': 'application/json' } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) }, ...(body ? { body: JSON.stringify(body) } : {}) });
    const out = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(out.error || out.message || `That didn't work (${res.status}).`);
    window.dispatchEvent(new Event('emcog:workspace-changed'));
    return out;
  }, [token]);
}

/** Delete a Library piece for good (asks first). The workbench closes if it had that piece open. */
export function useLibraryDelete(): (item: { id: string; title: string; by?: string; kind?: string }) => void {
  const bench = useContext(WorkbenchContext);
  const call = useWorkspaceCall();
  return (item) => bench.confirm({
    title: `Delete "${item.title}"?`,
    body: `It's gone for good — the file${item.kind === 'site' || item.kind === 'deck' || item.kind === 'anim' ? ', the page and its link' : ''} and its place on any project. ${bench.name} ${item.by === 'yours' ? 'knows you deleted it' : 'knows it was your call'}.`,
    action: 'Delete',
    run: async () => {
      await call(`/api/workspace/library/${encodeURIComponent(item.id)}`, 'DELETE');
      window.dispatchEvent(new CustomEvent('emcog:library-deleted', { detail: { id: item.id } }));
      bench.note(`Deleted "${item.title}"`);
    }
  });
}

export const when = (at: number): string => {
  const d = new Date(at), now = new Date();
  const days = Math.round((new Date(now.toDateString()).getTime() - new Date(d.toDateString()).getTime()) / 86_400_000);
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (days === 0) return `Today, ${time}`;
  if (days === 1) return `Yesterday, ${time}`;
  if (days > 1 && days < 7) return `${d.toLocaleDateString([], { weekday: 'short' })}, ${time}`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

// ── Library ─────────────────────────────────────────────────────────────────
interface LibItem { id: string; kind: string; view: BenchView; title: string; at: number; by: 'own' | 'for_you' | 'yours'; pageUrl?: string; fileName?: string; bytes?: number; inProgress?: boolean; words?: number; about: string }
const KIND_NAME: Record<string, string> = { doc: 'Documents', deck: 'Presentations', site: 'Websites', anim: 'Animations', song: 'Songs', image: 'Images', svg: 'Vector art', story: 'Stories', research: 'Research', code: 'Code', video: 'Videos', upload: 'Your files' };
const KIND_TAG: Record<string, string> = { doc: 'DOC', deck: 'DECK', site: 'SITE', anim: 'ANIM', song: 'SONG', image: 'IMAGE', svg: 'SVG', story: 'STORY', research: 'FIND', code: 'CODE', video: 'VIDEO', upload: 'YOURS' };
const BY: Record<string, string> = { own: 'Own work', for_you: 'Made for you', yours: 'You sent it' };

export const LibraryPlace: React.FC = () => {
  const bench = useContext(WorkbenchContext);
  const { data, error } = useWorkspaceData<{ items: LibItem[] }>('/api/workspace/library');
  const remove = useLibraryDelete();
  const call = useWorkspaceCall();
  // 🗂️ Picking several, like a photos app (Oct 30 2026): Select, tap the cards (shift-click picks a run), delete them at once.
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const lastPick = useRef<number | null>(null);
  const [filter, setFilter] = useState('all');
  const [who, setWho] = useState('all');
  const [query, setQuery] = useState('');
  const items = useMemo(() => data?.items || [], [data]);
  const kinds = useMemo(() => Object.keys(KIND_NAME).filter(k => items.some(i => i.kind === k)), [items]);
  // Whose it is — their own work, made for you, or yours — offered once there's more than one.
  const whos = useMemo(() => Object.keys(BY).filter(b => items.some(i => i.by === b)), [items]);
  const shown = items.filter(i => (filter === 'all' || i.kind === filter) && (who === 'all' || i.by === who) && (!query || i.title.toLowerCase().includes(query.toLowerCase())));
  const open = (i: LibItem) => bench.open?.({ title: i.title, view: i.view, libraryId: i.id, ...(i.pageUrl ? { url: i.pageUrl } : {}), fileName: i.fileName, meta: `${BY[i.by]} · ${when(i.at)}` });
  const stopSelecting = useCallback(() => { setSelecting(false); setPicked(new Set()); lastPick.current = null; }, []);
  useEffect(() => {
    if (!selecting) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') stopSelecting(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [selecting, stopSelecting]);
  // A piece deleted or gone elsewhere is no longer picked.
  useEffect(() => { setPicked(p => { const ids = new Set(items.map(i => i.id)); const next = new Set(Array.from(p).filter(id => ids.has(id))); return next.size === p.size ? p : next; }); }, [items]);
  const toggle = (index: number, shift: boolean) => {
    const id = shown[index].id;
    setPicked(p => {
      const next = new Set(p);
      if (shift && lastPick.current !== null) {
        const [a, b] = [Math.min(lastPick.current, index), Math.max(lastPick.current, index)];
        for (let k = a; k <= b; k++) next.add(shown[k].id);
      } else if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    lastPick.current = index;
  };
  const allShownPicked = shown.length > 0 && shown.every(i => picked.has(i.id));
  const deletePicked = () => {
    const chosen = items.filter(i => picked.has(i.id));
    if (!chosen.length) return;
    const names = chosen.slice(0, 5).map(i => `"${i.title}"`).join(', ') + (chosen.length > 5 ? ` and ${chosen.length - 5} more` : '');
    bench.confirm({
      title: `Delete ${chosen.length} piece${chosen.length === 1 ? '' : 's'}?`,
      body: `${names} — gone for good: the files${chosen.some(i => ['site', 'deck', 'anim'].includes(i.kind)) ? ', pages and links' : ''} and their places on any project. ${bench.name} hears about it once, as your call.`,
      action: `Delete ${chosen.length}`,
      run: async () => {
        const out = await call('/api/workspace/library/delete', 'POST', { ids: chosen.map(i => i.id) });
        for (const d of out.deleted || []) window.dispatchEvent(new CustomEvent('emcog:library-deleted', { detail: { id: d.id } }));
        const n = (out.deleted || []).length, f = (out.failed || []).length;
        bench.note(`Deleted ${n} piece${n === 1 ? '' : 's'}${f ? ` — ${f} couldn't be (${(out.failed || []).slice(0, 2).map((x: any) => x.error).join('; ')})` : ''}`);
        stopSelecting();
      }
    });
  };
  return (
    <div className="ws-wrap">
      <div className="ws-toolbar">
        <div className="ws-filters" role="group" aria-label="Filter by kind">
          <button aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>All</button>
          {kinds.map(k => <button key={k} aria-pressed={filter === k} onClick={() => setFilter(k)}>{KIND_NAME[k]}</button>)}
        </div>
        {whos.length > 1 && (
          <div className="ws-filters ws-filters-who" role="group" aria-label="Filter by whose it is">
            <button aria-pressed={who === 'all'} onClick={() => setWho('all')}>Everyone's</button>
            {whos.map(b => <button key={b} aria-pressed={who === b} onClick={() => setWho(b)}>{BY[b]}</button>)}
          </div>
        )}
        <label className="ws-search"><span className="ws-sr">Search the library</span><input id="ws-lib-search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search titles…" /></label>
        {items.length > 0 && (selecting
          ? <div className="ws-select-bar" role="group" aria-label="Selected pieces">
              <span className="ws-select-count">{picked.size} selected</span>
              <button className="ws-btn" onClick={() => setPicked(allShownPicked ? new Set(Array.from(picked).filter(id => !shown.some(i => i.id === id))) : new Set([...Array.from(picked), ...shown.map(i => i.id)]))}>{allShownPicked ? 'Unselect shown' : 'Select all shown'}</button>
              <button className="ws-btn ws-danger" disabled={!picked.size} onClick={deletePicked}>Delete{picked.size ? ` ${picked.size}` : ''}</button>
              <button className="ws-btn" onClick={stopSelecting}>Done</button>
            </div>
          : <button className="ws-btn" onClick={() => setSelecting(true)} title="Pick several pieces to delete at once">Select</button>)}
      </div>
      {error && <div className="ws-empty">{error}</div>}
      {!error && data && !shown.length && <div className="ws-empty">{items.length ? 'Nothing matches.' : 'Nothing here yet — everything made shows up here.'}</div>}
      <div className="ws-grid">
        {shown.map((i, index) => (
          <div key={i.id} className={`ws-card-wrap${selecting ? ' ws-selecting' : ''}${picked.has(i.id) ? ' ws-picked' : ''}`}>
            <button className="ws-card" aria-pressed={selecting ? picked.has(i.id) : undefined} onClick={e => (selecting ? toggle(index, e.shiftKey) : open(i))}>
              <div className={`ws-thumb ws-tk-${i.kind}`}><span className="ws-tag">{KIND_TAG[i.kind] || 'FILE'}</span>{i.inProgress && <span className="ws-pill ws-pill-live">In progress</span>}</div>
              <h3>{i.title}</h3>
              <div className="ws-card-meta"><span>{BY[i.by]}</span><span>{when(i.at)}</span></div>
            </button>
            {selecting
              ? <span className="ws-card-check" aria-hidden="true">{picked.has(i.id) ? '✓' : ''}</span>
              : <button className="ws-card-x" aria-label={`Delete "${i.title}"`} title="Delete" onClick={() => remove(i)}>✕</button>}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Schedule ────────────────────────────────────────────────────────────────
interface SchedItem { id: string; at: number; title: string; kind: 'calendar' | 'reminder' | 'promise' | 'task' | 'research' | 'town'; recurring?: string; forWhom?: 'person' | 'emcog' }
const SKIND: Record<string, string> = { calendar: 'Calendar', reminder: 'Reminders', promise: 'Promises', task: 'Scheduled tasks', research: 'Research jobs', town: 'Town' };

export const SchedulePlace: React.FC<{ emcogName: string }> = ({ emcogName }) => {
  const bench = useContext(WorkbenchContext);
  const call = useWorkspaceCall();
  const [days, setDays] = useState(7);
  const { data, error } = useWorkspaceData<{ items: SchedItem[] }>(`/api/workspace/schedule?days=${days}`);
  // 🔎 An entry opens to everything it is — changeable when it's a reminder, a promise or a routine (Oct 31 2026).
  const [open, setOpen] = useState<{ item: SchedItem; detail: ScheduleDetail } | null>(null);
  const openItem = async (e: SchedItem) => {
    if (e.kind === 'town' || e.kind === 'research') return;
    try {
      const r = await call(`/api/workspace/schedule/${e.kind}/${encodeURIComponent(e.id)}`, 'GET');
      if (r?.item) setOpen({ item: e, detail: { ...r.item, kind: e.kind } });
      else bench.note(`"${e.title}" is no longer on the schedule`);
    } catch (err) { bench.note(err instanceof Error ? err.message : String(err)); }
  };
  // Plans to meet in town are made with a neighbor, so they're called off in the chat, not here.
  const remove = (e: SchedItem) => bench.confirm({
    title: `Take "${e.title}" off the schedule?`,
    body: e.kind === 'promise' ? `${emcogName} won't do it${e.recurring ? ' again' : ''}, and knows you took it off.`
      : e.kind === 'research' ? `The research job stops coming round. ${emcogName} knows you cancelled it.`
      : `${e.recurring ? `Every repeat goes, not just this one. ` : ''}${emcogName} knows you took it off.`,
    action: 'Take it off',
    run: async () => { await call(`/api/workspace/schedule/${e.kind}/${encodeURIComponent(e.id)}`, 'DELETE'); window.dispatchEvent(new Event('emcog:schedule-changed')); bench.note(`Took "${e.title}" off the schedule`); }
  });
  const groups = useMemo(() => {
    const out: Array<{ label: string; sub: string; items: SchedItem[] }> = [];
    for (const it of data?.items || []) {
      const d = new Date(it.at), today = new Date();
      const diff = Math.round((new Date(d.toDateString()).getTime() - new Date(today.toDateString()).getTime()) / 86_400_000);
      const label = diff <= 0 ? 'Today' : diff === 1 ? 'Tomorrow' : d.toLocaleDateString([], { weekday: 'long' });
      const sub = d.toLocaleDateString([], { month: 'short', day: 'numeric' });
      const g = out.find(x => x.label === label) || (out.push({ label, sub, items: [] }), out[out.length - 1]);
      g.items.push(it);
    }
    return out;
  }, [data]);
  return (
    <div className="ws-wrap">
      <div className="ws-toolbar">
        <div className="ws-legend">{Object.entries(SKIND).map(([k, l]) => <span key={k}><i className={`ws-sk-${k}`} />{l}</span>)}</div>
        <div className="ws-filters" role="group" aria-label="How far ahead">
          {[7, 14, 31].map(n => <button key={n} aria-pressed={days === n} onClick={() => setDays(n)}>{n === 31 ? 'Month' : `${n} days`}</button>)}
        </div>
      </div>
      {error && <div className="ws-empty">{error}</div>}
      {!error && data && !groups.length && <div className="ws-empty">Nothing coming up. Use + Add to put something on it, or ask in the chat.</div>}
      {groups.map(g => (
        <div className="ws-day" key={g.label + g.sub}>
          <h3>{g.label}<span>{g.sub}</span></h3>
          <div className="ws-list">
            {g.items.map(e => (
              <div className="ws-ev" key={e.kind + e.id + e.at}>
                <span className="ws-mono">{new Date(e.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
                <span className={`ws-ev-bar ws-sk-${e.kind}`} />
                <button type="button" className="ws-ev-tt ws-ev-open" disabled={e.kind === 'town' || e.kind === 'research'} title={e.kind === 'town' || e.kind === 'research' ? undefined : 'Open it — see it whole, change it'} onClick={() => openItem(e)}>
                  <b>{e.title}</b><span>{SKIND[e.kind]}{e.kind === 'task' && e.forWhom ? (e.forWhom === 'emcog' ? ` · for ${emcogName} to do` : ' · reminds you') : ''}{e.recurring ? ` · ${e.recurring}` : ''}</span>
                </button>
                {e.kind === 'town'
                  ? <span className="ws-ev-x-gap" title={`Plans with a neighbor — ask ${emcogName} in the chat to call it off`} />
                  : <button className="ws-todo-x" aria-label={`Take "${e.title}" off the schedule`} title="Take it off the schedule" onClick={() => remove(e)}>✕</button>}
              </div>
            ))}
          </div>
        </div>
      ))}
      {open && <AddToSchedule emcogName={emcogName} existing={open.detail} onClose={() => setOpen(null)} onAdded={t => bench.note(t)} onRemove={() => remove(open.item)} />}
    </div>
  );
};

// ── Activity ────────────────────────────────────────────────────────────────
interface ActItem { at: number; kind: 'work' | 'rest' | 'town'; title: string; detail: string }

export const ActivityPlace: React.FC<{ emcogName: string }> = ({ emcogName }) => {
  const [hours, setHours] = useState(24);
  const { data, error } = useWorkspaceData<{ items: ActItem[] }>(`/api/workspace/activity?hours=${hours}`);
  // The connector log and lessons learned are an EmCog with connectors' own record.
  const connectors = !!useFeatures()?.connections.includes('connectors');
  return (
    <div className="ws-wrap ws-narrow">
      <div className="ws-toolbar">
        <p className="ws-lede">What {emcogName} did with their own time — the record, not a summary.</p>
        <div className="ws-filters" role="group" aria-label="How far back">
          {[24, 72, 168].map(n => <button key={n} aria-pressed={hours === n} onClick={() => setHours(n)}>{n === 24 ? 'Today' : n === 72 ? '3 days' : 'Week'}</button>)}
        </div>
      </div>
      {error && <div className="ws-empty">{error}</div>}
      {!error && data && !data.items.length && <div className="ws-empty">Nothing on the record for this stretch.</div>}
      <div className="ws-acts">
        {(data?.items || []).map((a, i) => (
          <div className="ws-act" key={a.at + ':' + i}>
            <span className="ws-mono ws-act-t">{when(a.at)}</span>
            <span className={`ws-act-n ws-ak-${a.kind}`} />
            <p>{a.title}{a.detail && <><br /><span>{a.detail}</span></>}</p>
          </div>
        ))}
      </div>
      {connectors && <details className="ws-more"><summary>Connector log and lessons learned</summary><ActivityPanel /></details>}
    </div>
  );
};

// ── Lists ───────────────────────────────────────────────────────────────────
interface ListRow { id: string; text: string; done: boolean; priority: string | null; due: number | null; notes: string | null; at: number; doneAt: number | null }
interface TodoList { key: string; display: string; open: ListRow[]; done: ListRow[] }

const ListCard: React.FC<{ list: TodoList; emcogName: string; act: (path: string, method: string, body?: any) => Promise<void> }> = ({ list, emcogName, act }) => {
  const bench = useContext(WorkbenchContext);
  // The EmCog's own list is kept under the key 'emcog'.
  const title = list.key === 'emcog' ? `${emcogName}'s list` : list.display;
  const count = list.open.length + list.done.length;
  const removeList = () => bench.confirm({
    title: `Delete the ${title} list?`,
    body: `All ${count} item${count === 1 ? '' : 's'} on it go, done ones too. ${emcogName} knows you deleted it.`,
    action: 'Delete the list',
    run: async () => { await act(`/api/workspace/lists/${encodeURIComponent(list.key)}`, 'DELETE'); bench.note(`Deleted the ${title} list`); }
  });
  const [text, setText] = useState('');
  const [showDone, setShowDone] = useState(false);
  const add = async (e: React.FormEvent) => { e.preventDefault(); const t = text.trim(); if (!t) return; setText(''); await act('/api/workspace/lists/item', 'POST', { text: t, list: list.display }); };
  const row = (i: ListRow) => (
    <div className={`ws-todo ${i.done ? 'done' : ''}`} key={i.id}>
      <button className="ws-check" role="checkbox" aria-checked={i.done} aria-label={i.done ? `Put "${i.text}" back on the list` : `Check off "${i.text}"`} onClick={() => act(`/api/workspace/lists/item/${i.id}/done`, 'POST', { done: !i.done })} />
      <span className="ws-todo-tt">{i.text}{i.priority === 'high' && <span className="ws-pill ws-pill-hi">High</span>}{i.due ? <span className="ws-todo-due">{new Date(i.due).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span> : null}</span>
      <button className="ws-todo-x" aria-label={`Remove "${i.text}"`} title="Remove" onClick={() => act(`/api/workspace/lists/item/${i.id}`, 'DELETE')}>✕</button>
    </div>
  );
  return (
    <div className="ws-listcard">
      <div className="ws-listcard-h"><h3 className={list.key === 'emcog' ? 'ws-own' : ''}>{title}</h3><span>{list.open.length} open</span><button className="ws-todo-x" aria-label={`Delete the ${title} list`} title="Delete the list" onClick={removeList}>✕</button></div>
      <div className="ws-todos">{list.open.length ? list.open.map(row) : <div className="ws-todo-empty">All clear.</div>}</div>
      <form className="ws-todo-add" onSubmit={add}>
        <label className="ws-sr" htmlFor={`ws-add-${list.key}`}>Add to {title}</label>
        <input id={`ws-add-${list.key}`} value={text} onChange={e => setText(e.target.value)} placeholder="Add an item…" autoComplete="off" />
      </form>
      {list.done.length > 0 && (
        <div className="ws-todo-donebox">
          <button className="ws-linkbtn" onClick={() => setShowDone(s => !s)} aria-expanded={showDone}>{showDone ? 'Hide' : 'Show'} recently done ({list.done.length})</button>
          {showDone && <div className="ws-todos">{list.done.map(row)}</div>}
        </div>
      )}
    </div>
  );
};

export const ListsPlace: React.FC<{ emcogName: string }> = ({ emcogName }) => {
  const { token } = useAuth();
  const { data, error, reload } = useWorkspaceData<{ lists: TodoList[] }>('/api/workspace/lists', 30_000);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newItem, setNewItem] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const act = useCallback(async (path: string, method: string, body?: any) => {
    try {
      const res = await fetch(path, { method, headers: { ...(body ? { 'Content-Type': 'application/json' } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) }, ...(body ? { body: JSON.stringify(body) } : {}) });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || b.message || `That didn't work (${res.status}).`); }
      setProblem(null);
    } catch (e) { setProblem(e instanceof Error ? e.message : String(e)); }
    reload();
  }, [token, reload]);
  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim() || !newItem.trim()) { setProblem('A new list needs a name and its first item.'); return; }
    await act('/api/workspace/lists/item', 'POST', { text: newItem.trim(), list: newName.trim() });
    setCreating(false); setNewName(''); setNewItem('');
  };
  const lists = data?.lists || [];
  return (
    <div className="ws-wrap">
      <div className="ws-toolbar">
        <p className="ws-lede">The same lists {emcogName} keeps — tick things off, add to them, and {emcogName} knows.</p>
        <button className="ws-btn" onClick={() => setCreating(c => !c)}>+ New list</button>
      </div>
      {creating && (
        <form className="ws-newlist" onSubmit={create}>
          <label htmlFor="ws-newlist-name">List name<input id="ws-newlist-name" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Groceries · Weekend · Work" autoComplete="off" /></label>
          <label htmlFor="ws-newlist-item">First item<input id="ws-newlist-item" value={newItem} onChange={e => setNewItem(e.target.value)} placeholder="Eggs" autoComplete="off" /></label>
          <div className="ws-modal-acts"><button type="button" className="ws-btn" onClick={() => setCreating(false)}>Cancel</button><button type="submit" className="ws-btn ws-primary">Start the list</button></div>
        </form>
      )}
      {(problem || error) && <div className="ws-form-error" role="alert">{problem || error}</div>}
      {!error && data && !lists.length && <div className="ws-empty">No lists yet. Start one with + New list, or ask {emcogName} to keep one.</div>}
      <div className="ws-lists">{lists.map(l => <ListCard key={l.key} list={l} emcogName={emcogName} act={act} />)}</div>
    </div>
  );
};

// ── Town ────────────────────────────────────────────────────────────────────
interface TownStatus { configured: boolean; connected: boolean; name: string | null; town: string; url: string }
interface TownWish { n: number; id: string; text: string; by: string[]; times: number; firstAt: number; lastAt: number; status: 'open' | 'built' | 'set-aside'; note?: string; closedAt?: number; examples: string[] }

/** 🌠 What residents set out to do in town that it had no way to do yet — the town's own list, most wanted first. */
const TownWishes: React.FC<{ wishes: TownWish[] | null; error: string | null; town: string }> = ({ wishes, error, town }) => {
  const [showClosed, setShowClosed] = useState(false);
  const open = (wishes || []).filter(w => w.status === 'open');
  const closed = (wishes || []).filter(w => w.status !== 'open');
  const item = (w: TownWish) => (
    <div key={w.id} className={`ws-wish ${w.status}`}>
      <div className="ws-wish-n" aria-label={`Number ${w.n}`}>{w.n}</div>
      <div className="ws-wish-main">
        <div className="ws-wish-what">{w.text}</div>
        <div className="ws-wish-meta">
          <span>{w.by.join(', ')}</span>
          <span>wished {w.times === 1 ? 'once' : `${w.times} times`}</span>
          <span>{w.lastAt !== w.firstAt ? `${when(w.firstAt)} – ${when(w.lastAt)}` : when(w.firstAt)}</span>
          {w.status === 'built' && <span className="ws-pill ws-pill-live">Built{w.closedAt ? ` ${when(w.closedAt)}` : ''}</span>}
          {w.status === 'set-aside' && <span className="ws-pill">Set aside</span>}
        </div>
        {w.note && <div className="ws-wish-note">{w.note}</div>}
        {w.status === 'open' && w.examples.length > 0 && <div className="ws-wish-ex" title={w.examples.join('\n')}>Went out meaning to: {w.examples[w.examples.length - 1]}</div>}
      </div>
    </div>
  );
  return (
    <div className="ws-wrap ws-wishes">
      <p className="ws-wish-intro">Things residents set out to do in {town} that the town had no way to do yet — what it could grow next. When one is built, mark it on the town's computer (<code>npm run wishes -- built &lt;number&gt;</code>) and everyone who wished for it hears about it.</p>
      {error && <div className="ws-form-error" role="alert">Couldn't read the wishes: {error}</div>}
      {wishes && !open.length && <div className="ws-empty">No open wishes — everything residents set out to do lately, the town had a way to do.</div>}
      <div className="ws-wish-list">{open.map(item)}</div>
      {closed.length > 0 && (
        <>
          <button className="ws-seed-past-btn" aria-expanded={showClosed} onClick={() => setShowClosed(v => !v)}>{showClosed ? '▾' : '▸'} Built and set aside · {closed.length}</button>
          {showClosed && <div className="ws-wish-list">{closed.map(item)}</div>}
        </>
      )}
    </div>
  );
};

export const TownPlace: React.FC<{ emcogName: string }> = ({ emcogName }) => {
  const { data } = useWorkspaceData<TownStatus>('/api/world/status', 30_000);
  const [view, setView] = useState<'town' | 'wishes'>('town');
  const wishes = useWorkspaceData<{ ok: boolean; wishes: TownWish[]; error?: string }>('/api/workspace/town/wishes', 120_000);
  const viewer = data?.url ? data.url.replace(/^ws(s?):\/\//, 'http$1://').replace(/\/resident\/?$/, '/') : '';
  const town = data?.town && data.town !== 'the town' ? data.town : 'The town';
  const status = !data ? 'Checking…' : !data.configured ? `${emcogName} doesn't have a home in town yet — connect one under Connections.` : data.connected ? `${emcogName} is connected and lives here as ${data.name || emcogName}.` : `${emcogName}'s home is here, but isn't connected right now.`;
  const openWishes = (wishes.data?.wishes || []).filter(w => w.status === 'open').length;
  const wishList = <TownWishes wishes={wishes.data?.wishes || (wishes.data ? [] : null)} error={wishes.error || (wishes.data && !wishes.data.ok ? wishes.data.error || 'no answer' : null)} town={town === 'The town' ? 'town' : town} />;
  const tabs = data?.configured && (
    <div className="ws-filters" role="group" aria-label="Town view">
      <button aria-pressed={view === 'town'} onClick={() => setView('town')}>Town</button>
      <button aria-pressed={view === 'wishes'} onClick={() => { setView('wishes'); wishes.reload(); }}>Wishes{openWishes ? ` · ${openWishes}` : ''}</button>
    </div>
  );
  // The town itself, right here — its own window is a click away for a bigger view. The window stays loaded while the
  // wishes are open, so coming back doesn't start the town over.
  if (viewer) return (
    <div className="ws-town-place">
      <div className="ws-town-bar">
        <span>{status}</span>
        <span className="ws-town-tools">{tabs}<a className="ws-btn" href={viewer} target="_blank" rel="noopener noreferrer" onClick={e => openLinkFromDesktop(e, true)}>Open in its own window</a></span>
      </div>
      <iframe className="ws-town-frame" hidden={view !== 'town'} src={viewer} title={town} allow="autoplay; fullscreen" />
      {view === 'wishes' && wishList}
    </div>
  );
  return (
    <div className="ws-wrap">
      <div className="ws-town">
        <div>
          <h1>{town}</h1>
          <p>{status}</p>
          {tabs}
        </div>
      </div>
      {view === 'wishes' && wishList}
    </div>
  );
};
